import { QIcon_close_default, QIcon_search_default } from '@repo/icons/index'
import classNames from 'classnames'
import { debounce } from 'lodash'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import { STACK_ACTIVITY_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../modules/hooks/common/useIsMobile'
import useIsWebview from '../../../modules/hooks/common/useIsWebview'
import { s_blog } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import client from '../../../modules/sanityClient'
import urls from '../../../modules/urls'
import { useBlogStack } from '../../../pages/[lang]/webview/blog'
import { BLOG_SEARCH_TAB_QUERY } from '../../../sanity/groqQueries'
import { BLOG_SEARCH_TAB_QUERYResult } from '../../../sanity/types'

const highlightText = (text, highlight) => {
  // 정규표현식으로 검색어 찾기
  const parts = text.split(new RegExp(`(${highlight})`, 'gi'))
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === highlight.toLowerCase() ? <strong key={i}>{part}</strong> : part
      )}
    </span>
  )
}

const SearchTab = ({ isMobile, ref, value, onChange, handleClose, handleKeyDown }) => {
  const { t } = useTranslation()
  const focusRef = useRef(null)

  return (
    <div className={style.searchTabLayout(isMobile)}>
      <div className="ml-0 mr-[8px] self-center">
        <QIcon_search_default color="#80839E" width={24} height={24} />
      </div>

      <Textarea
        ref={focusRef}
        value={value}
        onChange={onChange}
        rows={1}
        placeholder={t(s_blog.search_bar.placeholder_default)}
        draggable={false}
        className={classNames(
          'Body2-14Md w-full flex-grow cursor-pointer resize-none rounded-[8px] border-none bg-gray-50 p-0 text-black !ring-0 focus:border-none focus:outline-none',
          !isMobile && 'Body1-16Md',
          isMobile && 'Body2-14Md'
        )}
        onKeyDown={(e) => handleKeyDown(e, value)}
      ></Textarea>

      {value && (
        <div className="cursor-pointer self-center rounded-full bg-gray-200 p-[2px]" onClick={handleClose}>
          <QIcon_close_default color="#FFFFFF" width={16} height={16} />
        </div>
      )}
    </div>
  )
}

interface IBlogSearchTab {
  isMobile?: boolean
}
const BlogSearchTab = ({ isMobile = false }: IBlogSearchTab) => {
  const router = useAppRouter()
  const isWebview = useIsWebview()
  const { t } = useTranslation()
  const [value, setValue] = useState('')
  const [result, setResult] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [click, setClick] = useState(false)
  const ref = useRef(null)
  const layoutRef = useRef(null)

  const fetch = async (value, lang) => {
    setLoading(true)

    const params = {
      value: value,
      lang: lang,
    }

    const posts: BLOG_SEARCH_TAB_QUERYResult[] = await client.fetch(BLOG_SEARCH_TAB_QUERY, params)

    setResult(posts)
    setLoading(false)
  }

  const debouncedFetch = useCallback(
    debounce((value) => fetch(value, router.query.lang), 300),
    []
  )
  const handleSetValue = (value) => {
    setValue(value)
    debouncedFetch(value)
  }
  const onChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    handleSetValue(e.target.value)
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
    setValue('')
  }

  const handleKeyDown = (event, keywords) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      setResult([])
      setOpen(false)
      setClick(false)
      router.push(urls.student.landing.blog.result(keywords))
    }
  }

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!(layoutRef?.current === event.target || layoutRef?.current?.contains(event.target))) {
        setOpen(false)
        setValue('')
      }
    }

    if (open) {
      document.addEventListener('click', handleOutsideClick)
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [open])

  const filteredResult = result.filter((e) => e._score > 0)

  const handelClickTab = () => {
    setClick(true)
  }

  if (isWebview) {
    return (
      <DefaultView
        value={value}
        layoutRef={layoutRef}
        ref={ref}
        onChange={onChange}
        handleClose={handleClose}
        handleKeyDown={handleKeyDown}
        loading={loading}
        filteredResult={filteredResult}
      />
    )
  } else if (isMobile) {
    if (click) {
      return (
        <div className={'fixed top-0 left-0 z-[99999999] h-screen w-screen  bg-white px-[16px]'}>
          <div className="relative mb-[20px] py-[13px] text-center">
            <div className="">검색</div>
            <div className="absolute top-[12.5px] right-[0px] cursor-pointer" onClick={() => setClick(false)}>
              <QIcon_close_default width={24} height={24} />
            </div>
          </div>
          <SearchTab
            isMobile={true}
            ref={ref}
            value={value}
            onChange={onChange}
            handleClose={handleClose}
            handleKeyDown={handleKeyDown}
          />
          <div className="h-screen w-full">
            {value.length > 1 ? (
              <div className="mt-[20px] flex w-full flex-col items-start gap-[8px] text-start">
                {loading ? (
                  <div className="Body2-14Md text-gray-400"> {t(s_blog.search_bar.placeholder_loading)}</div>
                ) : filteredResult.length > 0 ? (
                  filteredResult.map((item, i) => {
                    return (
                      <button
                        key={i}
                        className="flex w-full max-w-[688px] rounded-[10px] hover:bg-gray-50"
                        onClick={() => router.push(urls.student.landing.blog.detail(item.slug))}
                      >
                        <div className={style.searchResultMoWebElement}>{highlightText(item.title, value)}</div>
                      </button>
                    )
                  })
                ) : (
                  <div className="Body2-14Md text-gray-400">{t(s_blog.search_bar.placeholder_no_result)}</div>
                )}
              </div>
            ) : (
              <div className="mt-[20px]"></div>
            )}
          </div>
        </div>
      )
    } else {
      return (
        <div className="relative mt-[20px] mb-[32px] h-[50px]" ref={layoutRef} onClick={handelClickTab}>
          <SearchTab
            isMobile={isMobile}
            ref={ref}
            value={value}
            onChange={onChange}
            handleClose={handleClose}
            handleKeyDown={handleKeyDown}
          />
        </div>
      )
    }
  } else {
    return (
      <DefaultView
        value={value}
        layoutRef={layoutRef}
        ref={ref}
        onChange={onChange}
        handleClose={handleClose}
        handleKeyDown={handleKeyDown}
        loading={loading}
        filteredResult={filteredResult}
      />
    )
  }
}

export default BlogSearchTab

const Textarea = (props) => (
  <textarea
    className="Body2-14Md w-full cursor-pointer resize-none rounded-[8px] border-none bg-gray-50 p-0 text-black !ring-0 focus:border-none focus:outline-none"
    {...props}
  ></textarea>
)
const style = {
  searchTabLayout: (isMobile) => {
    return isMobile
      ? `border-1 flex w-full max-w-[688px] items-center rounded-[8px] border-gray-200 bg-gray-50 p-[12px] focus-within:border-purple-500`
      : `border-1 flex max-w-[688px] items-center rounded-[8px] border-gray-200 bg-gray-50 p-[12px] focus-within:border-purple-500`
  },
  searchResultLayout: `border-1 absolute z-20 mt-[4px] w-full max-w-[688px] rounded-[8px] bg-white p-[8px] shadow-[0_2px_8px_0_rgba(0,0,0,0.15)]`,
  searchResultElement: `Body1-16Md rounded-[4px] px-[16px] py-[8px] hover:bg-gray-50`,
  searchResultMoWebElement: ` rounded-[4px] p-[8px]`,
}

const DefaultView = ({ value, layoutRef, ref, onChange, handleClose, handleKeyDown, loading, filteredResult }) => {
  const isMobile = useIsMobile()
  const router = useRouter()
  const { t } = useTranslation()

  const { useBlogFlow } = useBlogStack()
  const { push } = useBlogFlow()
  const appRouter = useAppRouter({ stackPush: push })

  return (
    <div className="relative mt-[32px] mb-[48px] h-[50px]" ref={layoutRef}>
      <SearchTab
        isMobile={isMobile}
        ref={ref}
        value={value}
        onChange={onChange}
        handleClose={handleClose}
        handleKeyDown={handleKeyDown}
      />

      {open && value.length > 1 && (
        <div className={style.searchResultLayout}>
          <div className="flex w-full flex-col items-start text-start">
            {loading ? (
              <div className={classNames(style.searchResultElement, 'text-gray-400')}>
                {t(s_blog.search_bar.placeholder_loading)}
              </div>
            ) : filteredResult.length > 0 ? (
              filteredResult
                .filter((e) => e._score > 0)
                .map((item, i) => {
                  return (
                    <button
                      key={i}
                      className="flex w-full justify-start rounded-[4px] text-start hover:bg-gray-50"
                      onClick={() =>
                        appRouter.push(urls.student.landing.blog.detail(item.slug), {
                          stackActivity: STACK_ACTIVITY_NAME.BLOG_DETAIL_ACTIVITY,
                          stackSendData: {
                            slug: item.slug,
                          },
                        })
                      }
                    >
                      <div className={style.searchResultElement}>{highlightText(item.title, value)}</div>
                    </button>
                  )
                })
            ) : (
              <div className={classNames(style.searchResultElement, 'text-gray-400')}>
                {t(s_blog.search_bar.placeholder_no_result)}
              </div>
            )}
          </div>
        </div>
      )}
      <br />
    </div>
  )
}
